import React, {useEffect, useRef, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import styled from '@emotion/styled'
import isChromatic from 'chromatic'
import {MAPBOX_DEFAULT_ZOOM, MAPBOX_STYLE_PART} from 'config/map'
import {useIsCrawler} from 'hooks/useIsCrawler'
import {
  trackStaticLocationMapBoxDisplayed,
  trackStaticLocationMapBoxLoaded
} from 'middleware/analytics/actions/trackEvent'
import {
  markAccommodationPageLoad,
  setStaticMapImageLoaded
} from 'modules/accommodation/slice'
import {getLanguageCode} from 'modules/meta/selectors'
import {getMapLanguage} from 'utils/mapLanguage'

import useIsImageloaded from '@vio/atlas/hooks/useIsImageloaded'
import {SkeletonLoader} from '@vio/atlas/SkeletonLoader'
import {getDevicePixelRatio} from '@vio/core/src/_web/utils/browser/getDevicePixelRatio'
import {useDispatchTeamEvent} from '@vio/core/src/analytics/components/TeamContext'

import {SelectedElement} from './TrackLocationClick'

// fh-brand-assets bucket, hosted through CloudFront
const pinImagePath = (dpx: string) =>
  `https://d3qk6lo8yfdp1c.cloudfront.net/mapbox/map-pin${dpx}.png`

interface Props {
  lat: number
  lon: number
}

const MapBoxImageWrapper = styled('div')`
  width: 100%;
  height: 100%;
`

const MapBoxImage = styled('div')<{backgroundUrl: string}>`
  height: 100%;
  width: 100%;
  background-size: contain;
  background-image: ${({backgroundUrl}) => `url('${backgroundUrl}')`};
`

const baseUrl = 'https://api.mapbox.com/styles/v1'
const accessToken: string = process.env.NEXT_PUBLIC_SELECT_MAPBOX_API_KEY

const useNodeSize = () => {
  const ref = useRef(null)
  const [size, setSize] = useState<{width: number; height: number}>(null)

  useEffect(() => {
    const node = ref.current

    // minimum timeout is needed because hidden elements (till overlay slides in have 0,0 size)
    const timeout = setTimeout(() => {
      // Consider using ResizeObserver here when browser support is good enough
      const box = node.getBoundingClientRect()
      // in some rare cases width/height has fractions in their values,
      // which are not allowed in mapbox for image size params (rounding)
      setSize({
        width: Math.round(box.width),
        height: Math.round(box.height)
      })
    })
    return () => {
      clearTimeout(timeout)
    }
  }, [])

  return {sizeRef: ref, size}
}

export const StaticMapImage = ({lat, lon}: Props) => {
  const dispatch = useDispatch()
  const {sizeRef, size} = useNodeSize()
  const [mapUrl, setMapUrl] = useState<string>(null)
  const languageCode = useSelector(getLanguageCode)
  const dpr = getDevicePixelRatio()
  const {isLoaded} = useIsImageloaded(mapUrl)
  const dispatchTeamEvent = useDispatchTeamEvent()
  const isCrawler = useIsCrawler()

  useEffect(() => {
    if (mapUrl) {
      dispatchTeamEvent(trackStaticLocationMapBoxDisplayed())
    }
  }, [dispatchTeamEvent, mapUrl])

  useEffect(() => {
    if (isLoaded) {
      dispatch(setStaticMapImageLoaded(true))
      dispatch(markAccommodationPageLoad())
      dispatchTeamEvent(trackStaticLocationMapBoxLoaded())
    }

    return () => {
      dispatch(setStaticMapImageLoaded(false))
    }
  }, [dispatch, dispatchTeamEvent, isLoaded])

  useEffect(() => {
    if (!size || isCrawler) {
      return
    }

    // MapBox only accepts none or @2x
    const mapPinDprPosfix = dpr > 1.5 ? '@2x' : ''

    const overlay = `url-${encodeURIComponent(
      pinImagePath(mapPinDprPosfix)
    )}(${lon},${lat})`
    const lonLatZoom = `${lon},${lat},${MAPBOX_DEFAULT_ZOOM}`
    const sizePrefix = `${size.width}x${size.height}${mapPinDprPosfix}`
    const language = getMapLanguage(languageCode ?? 'en')

    if (lon && lat) {
      setMapUrl(
        [
          baseUrl,
          MAPBOX_STYLE_PART,
          'static',
          overlay,
          lonLatZoom,
          sizePrefix
        ].join('/') + `?access_token=${accessToken}&language=${language}`
      )
    }
  }, [lon, lat, size, dpr, languageCode, isCrawler])

  // Under Chromatic the Mapbox network image and the size-measuring timeout
  // resolve non-deterministically, so render a stable skeleton to avoid flaky snapshots.
  const isSnapshot = isChromatic()
  let mapContent: React.ReactNode = isSnapshot ? (
    <SkeletonLoader height="100%" />
  ) : (
    size && <SkeletonLoader height="100%" />
  )

  if (!isSnapshot && isLoaded) {
    mapContent = (
      <MapBoxImage
        backgroundUrl={mapUrl}
        data-id={SelectedElement.StaticMapBackground}
      />
    )
  }

  return <MapBoxImageWrapper ref={sizeRef}>{mapContent}</MapBoxImageWrapper>
}
